import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { SidebarProvider, SidebarTrigger, SidebarInset } from "@/components/ui/sidebar";
import { AppSidebar } from "./AppSidebar";
import { HeaderBalanceBar } from "./HeaderBalanceBar";
import { PendingInvitesModal } from "@/components/sharing/PendingInvitesModal";
import { DataViewProvider } from "@/components/shared/DataViewFilter";
import { ValuesVisibilityProvider } from "@/hooks/use-values-visibility";
import { UpgradeBanner } from "@/components/subscription/UpgradeBanner";
import { UpgradeModal } from "@/components/subscription/UpgradeModal";
import { SessionDisconnectedDialog } from "@/components/session/SessionDisconnectedDialog";
import { ProductTour } from "@/components/tour/ProductTour";
import { FeedbackButton } from "@/components/shared/FeedbackButton";
import { useSessionMonitor } from "@/hooks/use-session-monitor";
import { useOnboarding } from "@/hooks/use-onboarding";
import { useSubscription } from "@/hooks/use-subscription";
import { Loader2 } from "lucide-react";

export function AppLayout() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const { isDisconnected } = useSessionMonitor();
  const { onboarding, completeStep } = useOnboarding();
  const { isFreemium } = useSubscription();
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    let mounted = true;

    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!mounted) return;
      if (!user) {
        navigate("/auth", { replace: true });
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      if (event === "SIGNED_OUT" || !session) {
        navigate("/auth", { replace: true });
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [navigate]);

  // Show tour for users who haven't completed it
  useEffect(() => {
    if (
      onboarding &&
      onboarding.has_completed_onboarding &&
      !onboarding.has_completed_tour
    ) {
      setShowTour(true);
    }
  }, [onboarding]);

  const handleTourComplete = async () => {
    setShowTour(false);
    await completeStep("has_completed_tour");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <DataViewProvider>
      <ValuesVisibilityProvider>
        <SidebarProvider>
          <div className="min-h-screen flex w-full">
            <AppSidebar />
            <SidebarInset className="flex-1 min-w-0 overflow-x-hidden">
              {isFreemium && <UpgradeBanner />}
              <header className="flex h-14 items-center gap-4 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-4 sticky top-0 z-10">
                <SidebarTrigger className="-ml-1" />
                <HeaderBalanceBar />
              </header>
              <main className="flex-1">
                <Outlet />
              </main>
            </SidebarInset>
          </div>
          <PendingInvitesModal />
          <UpgradeModal />
          <SessionDisconnectedDialog open={isDisconnected} />
          {showTour && <ProductTour onComplete={handleTourComplete} />}
          <FeedbackButton />
        </SidebarProvider>
      </ValuesVisibilityProvider>
    </DataViewProvider>
  );
}
